// src/RoomForm.js
import React, { useState, useEffect, useRef } from 'react';
import { fileToDataURL } from './utils';
import LabResults from './LabResults';
import IAQSection from './IAQSection';
import { loadRoomAreas } from './loadRoomAreas';

const floorOptions   = ['1st Floor','2nd Floor','Basement','Attic','Other'];
const compassOptions = ['North','South','East','West','Northwest','Northeast','Southwest','Southeast','Other'];

export default function RoomForm({ roomData, onDataChange, onDeleteRoom }) {
  const [areaData, setAreaData] = useState(null);
  const obsCaptureRef = useRef(null);

  // Load the YAML once
  useEffect(() => {
    loadRoomAreas()
      .then(setAreaData)
      .catch(err => {
        console.error('Failed to load roomAreas.yml', err);
        setAreaData({ rooms: [], areas: {} });
      });
  }, []);

  // Don’t render until we have it
  if (!areaData) return null;

  // Destructure out the lists
  const {
    rooms: roomList = [],
    areas: {
      bathroom = [],
      kitchen  = [],
      laundry  = [],
      generic  = []
    } = {}
  } = areaData;

  // Unpack the current room’s data
  const {
    id, room, area, observations, observationsPhotos = [],
    labResults, iaqReadings, recommendations, requirements,
    floor, compassDirection
  } = roomData;

  const change = (field, value) =>
    onDataChange(id, { [field]: value });

  // Build the dynamic title
  const parts = [];
  if (floor)           parts.push(floor);
  if (compassDirection) parts.push(compassDirection);
  if (room)            parts.push(room);
  const title = (parts.join(' ') || `Room ${id}`) + (area ? ` – ${area}` : '');

  // Determine area options: specific + generic
  const safeRoom = (room || '').toLowerCase();
  let areaList = [];
  if (safeRoom.includes('bath'))    areaList = [...bathroom, ...generic];
  else if (safeRoom.includes('kitchen')) areaList = [...kitchen,  ...generic];
  else if (safeRoom.includes('laundry')) areaList = [...laundry,  ...generic];
  else                                 areaList = generic;
  areaList = [...new Set(areaList)]; // dedupe

  // Observations photos handlers
  const onObsPick = async e => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await fileToDataURL(file);
    change('observationsPhotos', [...observationsPhotos, { url }]);
    e.target.value = null;
  };
  const removeObsPhoto = idx => {
    if (!window.confirm('Delete this observation photo?')) return;
    change('observationsPhotos', observationsPhotos.filter((_,i) => i!==idx));
  };

  // Lab results handlers
  const addLab = () =>
    change('labResults', [
      ...labResults,
      { sampleSerial:'', sampleMethod:'', airSampleType:'', minutes:'', roomArea:title, extensiveSubstantial:'', moldName:[] }
    ]);
  const updLab = (i, f, v) => {
    const arr = [...labResults]; arr[i][f] = v;
    change('labResults', arr);
  };
  const delLab = i => {
    if (!window.confirm('Delete this lab result?')) return;
    change('labResults', labResults.filter((_,j) => j!==i));
  };
  const moldChange = (i, opts) => {
    const arr = [...labResults];
    arr[i].moldName = Array.from(opts).map(o => o.value);
    change('labResults', arr);
  };

  // IAQ handlers
  const toggleIAQ = () =>
    change('iaqReadings', iaqReadings ? null : {
      temperature:'', relativeHumidity:'', aqi:'', temperatureHumidityPicture:null,
      pm25:'', pm10:'', particleCount:'', pmPicture:null,
      formaldehyde:'', tvoc:'', formaldehydeTvocPictures:[], co2AqiPictures:[]
    });
  const updIAQ = (f,v) => change('iaqReadings', { ...iaqReadings, [f]: v });

  return (
    <div className="border border-gray-300 p-4 mb-4 rounded-lg">
      {/* Delete Room */}
      <button
        className="bg-red-500 text-white px-3 py-1 rounded mb-4"
        onClick={() => window.confirm(`Delete Room ${id}?`) && onDeleteRoom(id)}
      >
        Delete This Room
      </button>

      {/* Title */}
      <h1 className="text-2xl font-bold mb-4">{title}</h1>

      {/* Room Selector */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <select
          className="flex-1 border rounded px-2 py-1"
          value={room}
          onChange={e => change('room', e.target.value)}
        >
          <option value="">Select Room…</option>
          {roomList.map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <input
          type="text"
          className="flex-1 border rounded px-2 py-1"
          placeholder="Or type custom room"
          value={room}
          onChange={e => change('room', e.target.value)}
        />
      </div>

      {/* Area Selector */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <select
          className="flex-1 border rounded px-2 py-1"
          value={area}
          onChange={e => change('area', e.target.value)}
        >
          <option value="">Select Area…</option>
          {areaList.map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <input
          type="text"
          className="flex-1 border rounded px-2 py-1"
          placeholder="Or type custom area"
          value={area}
          onChange={e => change('area', e.target.value)}
        />
      </div>

      {/* Floor & Compass */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <select
          className="flex-1 border rounded px-2 py-1"
          value={floor}
          onChange={e => change('floor', e.target.value)}
        >
          <option value="">Select Floor…</option>
          {floorOptions.map(f => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
        <select
          className="flex-1 border rounded px-2 py-1"
          value={compassDirection}
          onChange={e => change('compassDirection', e.target.value)}
        >
          <option value="">Select Direction…</option>
          {compassOptions.map(d => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      </div>

      {/* Observations */}
      <div className="mb-4">
        <label className="block font-medium mb-1">Observations:</label>
        <textarea
          className="w-full border rounded p-2"
          rows={5}
          value={observations}
          onChange={e => change('observations', e.target.value)}
        />
      </div>

      {/* Observation Photos */}
      <div className="mb-6">
        <label className="block font-medium mb-2">Observation Photos:</label>
        <div className="flex items-center gap-4 mb-2">
          <input type="file" accept="image/*" onChange={onObsPick} />
          <input
            ref={obsCaptureRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={onObsPick}
          />
          <button
            className="bg-green-500 text-white px-3 py-1 rounded"
            onClick={() => obsCaptureRef.current?.click()}
          >
            Take Photo
          </button>
        </div>
        <div className="flex flex-wrap">
          {observationsPhotos.map((pic, idx) => (
            <div key={idx} className="mr-4 mb-4">
              <img src={pic.url} alt="" className="max-w-xs mb-2 rounded" />
              <button
                className="bg-red-500 text-white px-3 py-1 rounded"
                onClick={() => removeObsPhoto(idx)}
              >
                Delete Photo
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Lab Results */}
      <LabResults
        labResults={labResults}
        addLab={addLab}
        updLab={updLab}
        delLab={delLab}
        moldChange={moldChange}
      />

      {/* IAQ Toggle & Section */}
      <div className="mb-4">
        <button
          className="px-3 py-1 bg-gray-200 text-gray-800 rounded"
          onClick={toggleIAQ}
        >
          {iaqReadings ? 'Remove Indoor Air Quality' : 'Add Indoor Air Quality'}
        </button>
      </div>
      {iaqReadings && <IAQSection iaqReadings={iaqReadings} updIAQ={updIAQ} />}

      {/* Recommendations */}
      <div className="mb-4">
        <label className="block font-medium mb-1">Recommendations:</label>
        <textarea
          className="w-full border rounded p-2"
          rows={4}
          value={recommendations}
          onChange={e => change('recommendations', e.target.value)}
        />
      </div>

      {/* Requirements */}
      <div className="mb-4">
        <label className="block font-medium mb-1">Remediation Requirements:</label>
        <textarea
          className="w-full border rounded p-2"
          rows={3}
          value={requirements.requirement}
          onChange={e =>
            change('requirements', {
              ...requirements,
              requirement: e.target.value
            })
          }
        />
        <div className="mt-2">
          <label className="block font-medium mb-1">Approx. FT²:</label>
          <input
            className="w-full border rounded px-2 py-1"
            value={requirements.approxFt}
            onChange={e =>
              change('requirements', {
                ...requirements,
                approxFt: e.target.value
              })
            }
          />
        </div>
      </div>
    </div>
);
}
